import React, { useCallback } from 'react';
import { FormattedNumber } from 'react-intl';
import cx from 'classnames';

import { DropdownMenu } from 'ui-components/components/dropdown-menu';
import { DropdownMenuItem } from 'ui-components/components/dropdown-menu-item';
import { DropdownMenuToggler } from 'ui-components/components/dropdown-menu-toggler';
import { Text, TextType } from 'ui-components/components/text';

import { COUPON, PROMOTION } from '../promotional-price/promotional-price';
import { getDiscountedValues } from '../../../migrate-plan-modal/helpers';
import { doesCouponBeatPromo } from '../../../../hooks/use-coupon';
import { messages } from './messages';
import styles from './plan-shipment-limit.module.less';
import { ShipmentLimitOptionContent } from './shipment-limit-option-content';
import { useShipmentLimitTiersForBillingPlan } from '../../../../hooks/use-plan-details';

/**
 * @param {object} props - Component props
 * @param {string} props.billingPlanId - The ID of the billing plan
 * @param {(billingPlanId: string, count: number) => void} props.onChange - Function to handle shipment count change
 * @param {number} props.selectedShipmentLimit - Currently selected shipment limit
 * @param {number | undefined} props.promoMinEligibleVolume - Minimum volume for promo discount eligibility
 * @param {number | undefined} props.couponMinEligibleVolume - Minimum volume for coupon discount eligibility
 * @param {number | undefined} props.promoDiscountPercent - Promo discount percent for tier markers
 * @param {number | undefined} props.couponDiscountPercent - Coupon discount percent for tier markers
 * @returns {React.JSX.Element} The rendered component
 */
export const PlanShipmentLimit = ({
  billingPlanId,
  onChange,
  selectedShipmentLimit,
  promoMinEligibleVolume,
  couponMinEligibleVolume,
  promoDiscountPercent,
  couponDiscountPercent,
}) => {
  const shipmentLimitTiers = useShipmentLimitTiersForBillingPlan(billingPlanId);

  // discountPercent: the applicable discount (null min = all tiers qualify).
  // discountType: only set when a min volume threshold exists and the tier meets it — drives the "X% off" badge.
  // the intent here being that non-XMigration coupons don't have a minVolume and thus apply to ALL volumes, so we don't
  // want to overwhelm users with a % Off marker on every line
  const getTierVariants = useCallback(
    (endingQuantity) => {
      const hasPromo = promoDiscountPercent != null;
      const hasCoupon = couponDiscountPercent != null;
      const meetsPromoMin = promoMinEligibleVolume == null || endingQuantity >= promoMinEligibleVolume;
      const meetsCouponMin = couponMinEligibleVolume == null || endingQuantity >= couponMinEligibleVolume;

      const promoApplies = hasPromo && meetsPromoMin;
      const couponApplies = hasCoupon && meetsCouponMin;
      const couponWins = promoApplies && couponApplies && doesCouponBeatPromo(couponDiscountPercent, promoDiscountPercent);

      let discountPercent = null;
      if (couponWins) discountPercent = couponDiscountPercent;
      else if (promoApplies) discountPercent = promoDiscountPercent;
      else if (couponApplies) discountPercent = couponDiscountPercent;

      let discountType = null;
      if (couponWins && couponMinEligibleVolume != null) discountType = COUPON;
      else if (promoApplies && promoMinEligibleVolume != null) discountType = PROMOTION;
      else if (couponApplies && couponMinEligibleVolume != null) discountType = COUPON;

      return { discountPercent, discountType };
    },
    [promoDiscountPercent, couponDiscountPercent, promoMinEligibleVolume, couponMinEligibleVolume],
  );

  const isShipmentLimitReadonly = shipmentLimitTiers.length <= 1;
  if (isShipmentLimitReadonly) {
    return (
      <div className={styles['shipment-limit-container']}>
        <Text
          className={styles['read-only-shipments']}
          formattedMessageValues={{
            count: <FormattedNumber value={selectedShipmentLimit} />,
          }}
          textType={TextType.Paragraph}
          value={messages.numberOfShipments}
        />
      </div>
    );
  }

  const selectedShipmentLimitTier = shipmentLimitTiers.find((tier) => tier.endingQuantity === selectedShipmentLimit);
  const selectedShipmentLimitCost = selectedShipmentLimitTier?.price || {};

  const someTierQualifiesPromo =
    promoDiscountPercent != null &&
    (promoMinEligibleVolume == null || shipmentLimitTiers.some((t) => t.endingQuantity >= promoMinEligibleVolume));
  const someTierQualifiesCoupon =
    couponDiscountPercent != null &&
    (couponMinEligibleVolume == null || shipmentLimitTiers.some((t) => t.endingQuantity >= couponMinEligibleVolume));
  const selectedQualifiesPromo =
    promoDiscountPercent != null && (promoMinEligibleVolume == null || selectedShipmentLimit >= promoMinEligibleVolume);
  const selectedQualifiesCoupon =
    couponDiscountPercent != null && (couponMinEligibleVolume == null || selectedShipmentLimit >= couponMinEligibleVolume);

  const shouldShowPromoBorder =
    someTierQualifiesPromo && !selectedQualifiesPromo && !doesCouponBeatPromo(couponDiscountPercent, promoDiscountPercent);
  const shouldShowCouponBorder = someTierQualifiesCoupon && !selectedQualifiesCoupon && !shouldShowPromoBorder;

  const { discountPercent: selectedTierDiscountPercent, discountType: selectedTierDiscountType } = getTierVariants(selectedShipmentLimit);
  const selectedTierDiscountedPrice =
    selectedTierDiscountPercent && selectedShipmentLimitCost?.value
      ? getDiscountedValues({ fullPrice: selectedShipmentLimitCost.value, discountPercentage: selectedTierDiscountPercent / 100 })
          .discountedPrice
      : null;

  return (
    <div className={styles['shipment-limit-container']}>
      <Text className={styles['shipment-dropdown-label']} textType={TextType.Label} value={messages.selectShipmentsLabel} />
      <DropdownMenu className={styles['shipment-dropdown']} dropdownMenuShouldHaveWidthOfButton variant='form'>
        <DropdownMenuToggler
          className={cx(styles['shipment-dropdown-toggler'], {
            [styles['shipment-dropdown-toggler-has-discount']]: selectedTierDiscountedPrice != null,
            [styles['promo-border']]: shouldShowPromoBorder,
            [styles['coupon-border']]: shouldShowCouponBorder,
          })}
          dropdownTogglerContentClassName={styles['shipment-limit-dropdown-toggler-content']}
        >
          <ShipmentLimitOptionContent
            discountPercent={selectedTierDiscountPercent}
            discountedPrice={selectedTierDiscountedPrice}
            discountType={selectedTierDiscountType}
            shipmentLimitCost={selectedShipmentLimitCost}
            shipmentLimitCount={selectedShipmentLimit}
            shouldShowIncludedForZeroCost
          />
        </DropdownMenuToggler>
        {shipmentLimitTiers.map(({ endingQuantity, price }) => {
          const { discountPercent: tierDiscountPercent, discountType: tierDiscountType } = getTierVariants(endingQuantity);
          const tierDiscountedPrice =
            tierDiscountPercent && price?.value
              ? getDiscountedValues({ fullPrice: price.value, discountPercentage: tierDiscountPercent / 100 }).discountedPrice
              : null;

          return (
            <DropdownMenuItem
              key={endingQuantity}
              className={styles['shipment-limit-dropdown-menu-item']}
              onClick={() => onChange(billingPlanId, endingQuantity)}
              selected={selectedShipmentLimit === endingQuantity}
            >
              <ShipmentLimitOptionContent
                discountPercent={tierDiscountPercent}
                discountedPrice={tierDiscountedPrice}
                discountType={tierDiscountType}
                shipmentLimitCost={price}
                shipmentLimitCount={endingQuantity}
                shouldShowIncludedForZeroCost={false}
              />
            </DropdownMenuItem>
          );
        })}
      </DropdownMenu>
    </div>
  );
};
